import React, { createContext, useContext, useEffect, useState } from "react";
import { api, getB2CToken, setB2CToken, clearB2CToken } from "@/lib/api";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { useLang } from "@/context/LanguageContext";

const B2CAuthContext = createContext(null);

export function B2CAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { lang, applyAccountLang } = useLang();

  // Apply the language saved on the account (cross-device persistence).
  useEffect(() => {
    const accountLang = user?.language;
    if (accountLang && accountLang !== lang) applyAccountLang(accountLang, user?.language_manual);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.language, user?.language_manual]);

  useEffect(() => {
    (async () => {
      const url = new URL(window.location.href);
      const rt = url.searchParams.get("rt");
      if (rt) {
        url.searchParams.delete("rt");
        window.history.replaceState(null, "", url.pathname + url.search + url.hash);
        if (!getB2CToken()) {
          try {
            const res = await api("/b2c/auth/resume", { method: "POST", body: { token: rt }, auth: false });
            setB2CToken(res.access_token);
          } catch { /* expired: user logs in again */ }
        }
      }
      if (getB2CToken()) {
        try {
          setUser(await api("/b2c/me", { b2c: true }));
        } catch {
          clearB2CToken();
        }
      }
      setLoading(false);
    })();
  }, []);

  async function login(email, password, remember = false) {
    const captcha_token = await getRecaptchaToken("b2c_login");
    const res = await api("/b2c/auth/login", { method: "POST", body: { email, password, captcha_token }, auth: false });
    setB2CToken(res.access_token, remember);
    setUser(res.user);
    return res.user;
  }

  async function register(payload, remember = false) {
    const captcha_token = await getRecaptchaToken("b2c_register");
    const res = await api("/b2c/auth/register", { method: "POST", body: { ...payload, captcha_token }, auth: false });
    setB2CToken(res.access_token, remember);
    setUser(res.user);
    return res.user;
  }

  async function forgot(email) {
    return api("/b2c/auth/forgot", { method: "POST", body: { email }, auth: false });
  }

  async function resetPassword(token, new_password) {
    return api("/b2c/auth/reset", { method: "POST", body: { token, new_password }, auth: false });
  }

  async function refreshUser() {
    try {
      const u = await api("/b2c/me", { b2c: true });
      setUser(u);
      return u;
    } catch { return null; }
  }

  function logout() {
    clearB2CToken();
    setUser(null);
  }

  return (
    <B2CAuthContext.Provider value={{ user, loading, login, register, logout, forgot, resetPassword, refreshUser, setUser }}>
      {children}
    </B2CAuthContext.Provider>
  );
}

export function useB2C() {
  const c = useContext(B2CAuthContext);
  if (!c) throw new Error("B2CAuthProvider missing");
  return c;
}
